import { useEffect, useLayoutEffect, useState } from 'react'

const useBrowserLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect
const OFFSET_PROPERTY = '--hero-viewport-offset'

// The header and skinny banner are flow siblings before #page-wrap. Measuring
// the hero's document offset includes their real height without following a
// sticky header or changing the stage when the customer scrolls.
const useDesktopHeroOffset = (isPhone) => {
  const [stage, setStage] = useState(null)

  useBrowserLayoutEffect(() => {
    const page = stage?.closest('#page-wrap')
    const layout = page?.parentElement
    if (isPhone || !layout) return undefined

    let disposed = false
    let previousOffset
    const updateOffset = () => {
      if (disposed) return
      let offset = 0
      let element = stage
      while (element) {
        offset += element.offsetTop
        element = element.offsetParent
      }
      if (!Number.isFinite(offset) || offset < 0 || offset === previousOffset) {
        return
      }
      stage.style.setProperty(OFFSET_PROPERTY, `${offset}px`)
      previousOffset = offset
    }

    const resizeObserver =
      typeof window.ResizeObserver === 'function'
        ? new window.ResizeObserver(updateOffset)
        : null
    let mutationObserver
    const observeLayout = () => {
      if (disposed) return
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
      // Watch late loading-banner replacement without watching Swiper/content
      // mutations or the CSS variable that this hook writes on the hero.
      mutationObserver?.observe(layout, { childList: true })
      let sibling = page.previousElementSibling
      while (sibling) {
        resizeObserver?.observe(sibling)
        if (!resizeObserver) {
          mutationObserver?.observe(sibling, {
            childList: true,
            subtree: true,
            characterData: true,
            attributes: true,
          })
        }
        sibling = sibling.previousElementSibling
      }
      updateOffset()
    }
    if (typeof window.MutationObserver === 'function') {
      mutationObserver = new window.MutationObserver(observeLayout)
    }

    observeLayout()
    window.addEventListener('resize', updateOffset)
    layout.addEventListener('load', updateOffset, true)
    document.fonts?.addEventListener?.('loadingdone', updateOffset)

    return () => {
      disposed = true
      resizeObserver?.disconnect()
      mutationObserver?.disconnect()
      window.removeEventListener('resize', updateOffset)
      layout.removeEventListener('load', updateOffset, true)
      document.fonts?.removeEventListener?.('loadingdone', updateOffset)
      stage.style.removeProperty(OFFSET_PROPERTY)
    }
  }, [isPhone, stage])

  return setStage
}

export default useDesktopHeroOffset
